import {
  PaymentMethod,
  PaymentMethodTypes,
  StoredCreditCard,
} from '@accedo/one-plugin-sdk';
import { useEffect } from 'react';
import useSWR, { mutate } from 'swr';

import { sleep } from '#/service/utils';
import { useAuthStore } from '#/store/auth/auth.store';
import { usePaywallStore } from '#/store/paywall/paywall.store';

const SWR_CACHE_KEY = 'getPaymentDetails';

// Some providers seem to have their own caching, and can return old data for a while after a change. For example,
// Cleeng returns the old card for a few seconds after a 3DS card change. Trying to detect and work around this would be
// messy and unreliable, and would need multiple fetches again (the exact thing this hooks tries to avoid). But it's not
// critical to fetch the payment details immediately on page load, and we cache the response "forever", so a one time
// safety delay seems like an acceptable compromise.
const SAFETY_DELAY_MS = 5_000;

export const UNKNOWN_PAYMENT_METHOD: PaymentMethod = {
  type: PaymentMethodTypes.Unknown,
};

// Remove all getPaymentDetails responses from SWR cache, regardless of the remaining parts of the key array and
// trigger revalidation. We can't use the scoped `mutate` returned by `useSWR` because `PaymentMethod` unmount/remount
// can cancel our revalidation.
const invalidateCache = () => {
  mutate((key) => Array.isArray(key) && key[0] === SWR_CACHE_KEY, undefined, {
    revalidate: true,
  }).then();
};

// Handle backwards compatibility for paywalls that aren't updated to payment method change
// Remove this when paywalls return expected type with payment methods.
// Originally added in 2022: https://github.com/Accedo-Products/one-client-web/pull/896/changes#diff-3632677bf9aefa2df225b1950ff0c1b4fc3c79bdcb1a9d97e3e5708656f0804d
const fixOldPluginReturnTypes = (res: PaymentMethod) => {
  let paymentMethod = res;

  if (res && !res?.type) {
    paymentMethod = {
      type: PaymentMethodTypes.CreditCard,
      properties: res as unknown as StoredCreditCard,
    };
  }

  return paymentMethod;
};

/**
 * Hook for getting the current payment method via paywallApi.getPaymentDetails using SWR.
 * The response is cached, even through remounts and shared between callers (PaywallManager and PaymentMethod).
 * It automatically refetches if `isLoggedIn` or `entitlementsKey` changes.
 * It returns an `invalidateCache` function so we can manually trigger a refetch after changing the card for example.
 */
export const usePaymentMethod = () => {
  const { paywallApi } = usePaywallStore();
  const { isLoggedIn, entitlementsKey } = useAuthStore();

  const {
    data = UNKNOWN_PAYMENT_METHOD,
    isLoading,
    isValidating,
  } = useSWR<PaymentMethod>(
    [SWR_CACHE_KEY, isLoggedIn, entitlementsKey],
    async () => {
      if (!isLoggedIn || !paywallApi.getPaymentDetails) {
        return UNKNOWN_PAYMENT_METHOD;
      }

      await sleep(SAFETY_DELAY_MS);

      return paywallApi
        .getPaymentDetails()
        .then(fixOldPluginReturnTypes)
        .catch(() => UNKNOWN_PAYMENT_METHOD);
    },
    {
      // revalidateOnMount is intentionally unset here, meaning: it will fetch on first mount (when cache is empty),
      // but reuse previously cached data on subsequent mounts due to revalidateIfStale:false.
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  // invalidate on logout
  useEffect(() => {
    if (!isLoggedIn) {
      invalidateCache();
    }
  }, [isLoggedIn]);

  return {
    paymentMethod: data,
    isFetching: isLoading || isValidating,
    invalidateCache,
  };
};
